import { Color } from '@mui/material';
import {
  PaletteOptions,
  SimplePaletteColorOptions,
  alpha,
} from '@mui/material/styles';
import { Color as UserColor } from '../generated/graphql';

export type ColorSchema =
  | 'primary'
  | 'secondary'
  | 'info'
  | 'success'
  | 'warning'
  | 'error';

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string;
    support: string;
  }

  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }

  interface PaletteColor {
    lighter: string;
    darker: string;
  }

  interface PaletteOptions {
    userPreference: Record<UserColor, string>;
    pink: Color;
  }

  interface Palette {
    communicate: Color;
    collaborate: Color;
    ai: Color;
    drive: Color;
    userPreference: Record<UserColor, string>;
    pink: Color;
    avatar: Record<ColorSchema, { main: string; contrastText: string }>;
    default: Partial<Color>;
  }

  interface TypeAction {
    /**
     * action.hover without opacity mixed with background color
     */
    hoverSolid: string;
  }
}

// SETUP COLORS

const PINK = {
  0: '#FFFFFF',
  50: '#FDF2F8',
  100: '#FCE9F0',
  200: '#F8D0DF',
  300: '#F5B4CE',
  400: '#F192BA',
  500: '#EC4899',
  600: '#E14592',
  700: '#C93D83',
  800: '#AE3571',
  900: '#8E2B5C',
  A100: '#F8D0DF',
  A200: '#F192BA',
  A400: '#E14592',
  A700: '#C93D83',
};

const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
};

const COMMUNICATE = {
  light: {
    50: '#F4F3FA',
    100: '#EBE9F7',
    200: '#D4D0EE',
    300: '#BBB4E5',
    400: '#9E93DB',
    500: '#6449CC',
    600: '#5F46C3',
    700: '#553EAE',
    800: '#4A3697',
    900: '#3C2C7B',
    950: '#2B1F57',
  },
  dark: {
    50: '#201741',
    100: '#2B1F57',
    200: '#3C2C7B',
    300: '#4A3697',
    400: '#553EAE',
    500: '#6449CC',
    600: '#7A67D1',
    700: '#9E93DB',
    800: '#BBB4E5',
    900: '#D4D0EE',
    950: '#EBE9F7',
  },
};

const COLLABORATE = {
  light: {
    50: '#F2F5FC',
    100: '#E7EDF9',
    200: '#CCDAF2',
    300: '#ACC5EC',
    400: '#86AEE5',
    500: '#1183DA',
    600: '#107DD0',
    700: '#0F70BA',
    800: '#0D61A1',
    900: '#0A4F84',
    950: '#07385D',
  },
  dark: {
    50: '#052945',
    100: '#07385D',
    200: '#0A4F84',
    300: '#0D61A1',
    400: '#0F70BA',
    500: '#1183DA',
    600: '#4E93DE',
    700: '#86AEE5',
    800: '#ACC5EC',
    900: '#CCDAF2',
    950: '#E7EDF9',
  },
};

const AI = {
  light: {
    50: '#7D9FC0',
    100: '#6188AE',
    200: '#4E7093',
    300: '#405973',
    400: '#314254',
    500: '#212B36',
    600: '#1B252C',
    700: '#151D21',
    800: '#0F1517',
    900: '#080C0D',
    950: '#020303',
  },
  dark: {
    50: '#020303',
    100: '#080C0D',
    200: '#0F1517',
    300: '#212B36',
    400: '#1B252C',
    500: '#212B36',
    600: '#314254',
    700: '#405973',
    800: '#4E7093',
    900: '#6188AE',
    950: '#7D9FC0',
  },
};

const DRIVE = {
  light: {
    50: '#F2F8FA',
    100: '#E7F2F5',
    200: '#CCE3EB',
    300: '#ADD4E0',
    400: '#88C4D4',
    500: '#1FA9C2',
    600: '#1EA1B9',
    700: '#1A90A6',
    800: '#177D8F',
    900: '#136675',
    950: '#0D4853',
  },
  dark: {
    50: '#0A353D',
    100: '#0D4853',
    200: '#136675',
    300: '#177D8F',
    400: '#1A90A6',
    500: '#1FA9C2',
    600: '#52B2C8',
    700: '#88C4D4',
    800: '#ADD4E0',
    900: '#CCE3EB',
    950: '#E7F2F5',
  },
};

const PRIMARY: SimplePaletteColorOptions = {
  lighter: '#C8FACD',
  light: '#5BE584',
  main: '#00AB55',
  dark: '#007B55',
  darker: '#005249',
  contrastText: '#fff',
};

const SECONDARY: SimplePaletteColorOptions = {
  lighter: '#D6E4FF',
  light: '#84A9FF',
  main: '#3366FF',
  dark: '#1939B7',
  darker: '#091A7A',
  contrastText: '#fff',
};

const INFO: SimplePaletteColorOptions = {
  lighter: '#CAFDF5',
  light: '#61F3F3',
  main: '#00B8D9',
  dark: '#006C9C',
  darker: '#003768',
  contrastText: '#fff',
};

const SUCCESS: Record<string, SimplePaletteColorOptions> = {
  light: {
    lighter: '#E8F3ED',
    light: '#B0D9C3',
    main: '#36B37E',
    dark: '#2E996C',
    darker: '#216C4C',
    contrastText: '#fff',
  },
  dark: {
    lighter: '#174C36',
    light: '#28845D',
    main: '#36B37E',
    dark: '#8DCBAB',
    darker: '#CEE6D9',
    contrastText: '#fff',
  },
};

const WARNING: Record<string, SimplePaletteColorOptions> = {
  light: {
    lighter: '#FFF2E7',
    light: '#FFD5AC',
    main: '#FFAB00',
    dark: '#DA9200',
    darker: '#9A6700',
    contrastText: GREY[800],
  },
  dark: {
    lighter: '#6D4900',
    light: '#BC7E00',
    main: '#FFAB00',
    dark: '#FFC585',
    darker: '#FFE4CB',
    contrastText: GREY[800],
  },
};

const ERROR: Record<string, SimplePaletteColorOptions> = {
  light: {
    lighter: '#FFE8E8',
    light: '#FFB0B0',
    main: '#FF3636',
    dark: '#DA2E2E',
    darker: '#9A2121',
    contrastText: '#fff',
  },
  dark: {
    lighter: '#6D1717',
    light: '#BC2828',
    main: '#FF3636',
    dark: '#FF8D8D',
    darker: '#FFCECE',
    contrastText: '#fff',
  },
};

const COMMON: PaletteOptions = {
  common: { black: '#000', white: '#fff' },
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  // success: SUCCESS.light,
  // warning: WARNING.light,
  // error: ERROR.light,
  grey: GREY,
  pink: PINK,
  userPreference: {
    [UserColor.Gray]: '#B0B0B0',
    [UserColor.Blue]: '#3366FF',
    [UserColor.Green]: '#36B37E',
    [UserColor.GreenLemon]: '#cddc39',
    [UserColor.NavyBlue]: '#3f51b5',
    [UserColor.Orange]: '#ff9800',
    [UserColor.Purple]: '#9747FF',
    [UserColor.Salmon]: '#e57373',
    [UserColor.Wine]: '#c62828',
    [UserColor.Yellow]: '#ffee58',
    [UserColor.Red]: '#FF5630',
  },
  divider: alpha(GREY[500], 0.24),
  action: {
    hover: alpha(GREY[500], 0.08),
    selected: alpha(GREY[500], 0.16),
    disabled: alpha(GREY[500], 0.8),
    disabledBackground: alpha(GREY[500], 0.24),
    focus: alpha(GREY[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

// NOTE: set the background var to be read by the avatar component to create the transparent border effect
const getCustomBg = (color: string) => `${color}; --background-color: ${color}`;

export default function palette(themeMode: 'light' | 'dark') {
  const light = {
    ...COMMON,
    mode: 'light',
    success: SUCCESS.light,
    warning: WARNING.light,
    error: ERROR.light,
    text: {
      primary: GREY[800],
      secondary: GREY[600],
      disabled: GREY[500],
    },
    background: {
      paper: getCustomBg(GREY[200]),
      default: getCustomBg('#fff'),
      neutral: getCustomBg(GREY[100]),
      support: getCustomBg(GREY[100]),
    },
    avatar: {
      primary: { main: PRIMARY.main, contrastText: '#fff' },
      secondary: { main: SECONDARY.main, contrastText: '#fff' },
      info: { main: INFO.main, contrastText: '#fff' },
      success: { main: SUCCESS.light.main, contrastText: '#fff' },
      warning: { main: WARNING.light.main, contrastText: GREY[800] },
      error: { main: ERROR.light.main, contrastText: '#fff' },
    },
    action: {
      ...COMMON.action,
      active: GREY[600],
      hoverSolid: '#e8ecef',
    },
    communicate: COMMUNICATE.light,
    collaborate: COLLABORATE.light,
    ai: AI.light,
    drive: DRIVE.light,
  } as const;

  const dark = {
    ...COMMON,
    mode: 'dark',
    success: SUCCESS.dark,
    warning: WARNING.dark,
    error: ERROR.dark,
    text: {
      primary: GREY[0],
      secondary: GREY[600],
      disabled: GREY[500],
    },
    background: {
      paper: getCustomBg(GREY[800]),
      default: getCustomBg(GREY[900]),
      neutral: getCustomBg(alpha(GREY[500], 0.16)),
      support: getCustomBg('#1B222C'),
    },
    avatar: {
      primary: { main: PRIMARY.main, contrastText: '#fff' },
      secondary: { main: SECONDARY.main, contrastText: '#fff' },
      info: { main: INFO.main, contrastText: '#fff' },
      success: { main: SUCCESS.dark.main, contrastText: '#fff' },
      warning: { main: WARNING.dark.main, contrastText: GREY[800] },
      error: { main: ERROR.dark.main, contrastText: '#fff' },
    },
    action: {
      ...COMMON.action,
      active: GREY[500],
      hoverSolid: '#2a333c',
    },
    communicate: COMMUNICATE.dark,
    collaborate: COLLABORATE.dark,
    ai: AI.dark,
    drive: DRIVE.dark,
  } as const;

  return themeMode === 'light' ? light : dark;
}
